/**
 * UpgradePrompt — Phase 3 slices 1 + 2 plan-limit modal.
 *
 * Mounted globally. Pops whenever apiFetch sees a 403 / PLAN_LIMIT response
 * (the interceptor pushes a notice into the billing store).
 *
 * Slice 2: clicking "Upgrade" creates a Lemon Squeezy checkout session and
 * redirects to the hosted checkout URL. Enterprise still routes to mailto
 * since it's sales-led (no enterprise variant configured).
 */

import { useEffect, useState } from 'react';
import { useBillingStore } from '../../stores/billingStore';
import { PLAN_DISPLAY_ORDER, PLAN_TIERS, planLabel } from '../../shared/plans';
import type { PlanId } from '../../shared/plans';
import './UpgradePrompt.css';

const SUPPORT_EMAIL = 'support@soleadlab.com';

export default function UpgradePrompt() {
  const notice = useBillingStore((s) => s.lastPlanLimit);
  const clearPlanLimit = useBillingStore((s) => s.clearPlanLimit);
  const createCheckout = useBillingStore((s) => s.createCheckout);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // ESC closes the modal.
  useEffect(() => {
    if (!notice) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearPlanLimit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [notice, clearPlanLimit]);

  // Reset transient state when the modal closes.
  useEffect(() => {
    if (!notice) {
      setCheckoutLoading(false);
      setCheckoutError(null);
    }
  }, [notice]);

  if (!notice) return null;

  const currentPlan = (notice.planId as PlanId) || 'free';
  const recommended = recommendForFeature(notice.feature, currentPlan);

  // Enterprise stays sales-led for now; the variant isn't configured server-
  // side, so checkout would 400. Keep the mailto fallback for that case.
  const recommendedIsCheckout = recommended === 'professional';

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const url = await createCheckout(recommended === 'enterprise' ? 'enterprise' : 'professional');
      // Full-page redirect — Lemon's hosted checkout owns the next step.
      window.location.href = url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Checkout failed';
      setCheckoutError(message);
      setCheckoutLoading(false);
    }
  };

  const subject = encodeURIComponent('Plan upgrade request');
  const bodyLines = [
    `Org plan: ${planLabel(currentPlan)}`,
    `Requested plan: ${planLabel(recommended)}`,
    `Feature blocked: ${notice.feature}`,
    notice.limit !== undefined ? `Limit: ${notice.limit} (current: ${notice.current ?? 0})` : null,
    '',
    'I would like to upgrade to a higher plan.',
  ].filter(Boolean) as string[];
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${encodeURIComponent(bodyLines.join('\n'))}`;

  return (
    <div className="upgrade-prompt__backdrop" onClick={clearPlanLimit}>
      <div
        className="upgrade-prompt__card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="upgrade-prompt__header">
          <h2 className="upgrade-prompt__title">Upgrade required</h2>
          <button
            type="button"
            className="upgrade-prompt__close"
            onClick={clearPlanLimit}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="upgrade-prompt__body">
          <p className="upgrade-prompt__message">{notice.message}</p>

          <div className="upgrade-prompt__plans">
            {PLAN_DISPLAY_ORDER.map((planId) => {
              const features = PLAN_TIERS[planId];
              const isCurrent = planId === currentPlan;
              const isRecommended = planId === recommended;
              return (
                <div
                  key={planId}
                  className={
                    'upgrade-prompt__plan'
                    + (isCurrent ? ' upgrade-prompt__plan--current' : '')
                    + (isRecommended ? ' upgrade-prompt__plan--recommended' : '')
                  }
                >
                  <div className="upgrade-prompt__plan-head">
                    <span className="upgrade-prompt__plan-name">{planLabel(planId)}</span>
                    {isCurrent && <span className="upgrade-prompt__plan-tag">Current</span>}
                    {isRecommended && !isCurrent && (
                      <span className="upgrade-prompt__plan-tag upgrade-prompt__plan-tag--rec">
                        Recommended
                      </span>
                    )}
                  </div>
                  <ul className="upgrade-prompt__plan-list">
                    <li>
                      <strong>{formatLimit(features.maxSites)}</strong> sites
                    </li>
                    <li>
                      <strong>{formatLimit(features.maxUsers)}</strong> members
                    </li>
                    <li>
                      <strong>{formatLimit(features.maxStorageGb, 'GB')}</strong> storage
                    </li>
                    <li className={features.scada ? 'on' : 'off'}>
                      {features.scada ? 'SCADA monitoring' : 'No SCADA monitoring'}
                    </li>
                    <li className={features.iv ? 'on' : 'off'}>
                      {features.iv ? 'IV reporting' : 'No IV reporting'}
                    </li>
                    <li className={features.advancedAlarms ? 'on' : 'off'}>
                      {features.advancedAlarms ? 'Advanced alarms' : 'No advanced alarms'}
                    </li>
                  </ul>
                </div>
              );
            })}
          </div>

          {checkoutError && (
            <div className="upgrade-prompt__error">
              {checkoutError}
            </div>
          )}
        </div>

        <footer className="upgrade-prompt__footer">
          <button
            type="button"
            className="upgrade-prompt__secondary"
            onClick={clearPlanLimit}
          >
            Maybe later
          </button>
          {recommendedIsCheckout ? (
            <button
              type="button"
              className="upgrade-prompt__primary"
              onClick={handleCheckout}
              disabled={checkoutLoading}
            >
              {checkoutLoading
                ? 'Opening checkout…'
                : `Upgrade to ${planLabel(recommended)}`}
            </button>
          ) : (
            <a className="upgrade-prompt__primary" href={mailto}>
              Contact sales to upgrade
            </a>
          )}
        </footer>
      </div>
    </div>
  );
}

function recommendForFeature(feature: string, currentPlan: PlanId): PlanId {
  // For boolean features, jump straight to the lowest plan that includes them.
  if (feature === 'scada' || feature === 'iv' || feature === 'advancedAlarms') {
    if (PLAN_TIERS.professional[feature]) return 'professional';
    return 'enterprise';
  }
  // Numeric features: bump up one tier.
  return currentPlan === 'free' ? 'professional' : 'enterprise';
}

function formatLimit(value: number, suffix?: string): string {
  if (value === -1) return 'Unlimited';
  return suffix ? `${value} ${suffix}` : String(value);
}
